import { ApiError, OFFLINE, type Api } from "../api/client.js";
import type { KycConfirmed, Receipt } from "../api/types.js";
import { KycFlowView, type StartNote } from "../ui/kyc.js";
import { ADD_COPY } from "./add-payee-model.js";
import type { CardSigner, Prepared, SignResult } from "./card-signer.js";
import { doneModel, fundsOffered, kycCardOf, KYC_COPY, occupationProblem, personRows, startHero, type KycCard } from "./kyc-model.js";
import type { RefreshProof } from "./judge-model.js";

/**
 * DCTA-176 F45/F46 — the identity-check refresh, the customer's side (docs/design/v3/04-kyc.md §0.2 Interactions, §2.2,
 * §2.8; backend B58: three routes and a signature).
 *
 *  1. Start (`POST /v1/kyc/refresh`): what the demo Myinfo would share and when the bank last checked. Already up to date
 *     (409) → Done at once. No refresh route on this bank (404, not demo mode) → the caller opens Talk to us, as before.
 *  2. "Get my details from Myinfo" → the consent placeholder. Share → `…/myinfo {consent: given}` → Details; Cancel →
 *     `{consent: declined}` → Start with 25i's line. Myinfo down (503) → Start's loud line.
 *  3. Review (an occupation, at least one source) → `…/declaration` → the card, drawn from `GET /v1/bundles/{id}` and
 *     nothing else (shown = signed), signed through the gateway like any card (CardSigner: the ceremony is the tap's first
 *     asynchronous act; one ceremony at a time). The bank builds ONE live card per refresh: back from Check and sign
 *     cancels the card and — DCTA-198, 176-zz — the next Review declares on the same refresh (the bank takes a refresh whose
 *     card is dead), no second Myinfo consent; once that cancel is at the bank. Would the bank not take it (422, 410), the
 *     next card needs a fresh refresh and asks Myinfo's consent again — never a consent the customer did not give.
 *  4. Signed → Done (25j) from the confirm's `kyc` answer; Done hands that answer to the caller (row 24d, Ask again).
 *
 * Every card left behind (Not now, back, a close, a card nobody waited for) is cancelled at the bank, so nothing is left
 * that could be signed. Nothing here moves money; the declaration is the customer's own, signed with their passkey.
 */
export type KycFlowApi = Pick<Api, "kycRefreshStart" | "kycMyinfo" | "kycDeclaration" | "bundle" | "cancelBundle">;
export type KycSigner = Pick<CardSigner, "prepare" | "sign" | "signing">;

export interface KycFlowDeps {
  readonly api: KycFlowApi;
  /** The check's status as the session knows it ("restricted", "refresh_due", …): Start's words. */
  readonly status: string;
  /** The signer for one card (CardSigner in the app; a fake in a test). */
  readonly signer: (bundleId: string) => KycSigner;
  /** Talk to us — the bank's number and hours, on the support sheet. */
  readonly onTalk: () => void;
  /** The flow is over: Done with the bank's answer, or left with nothing saved (undefined). */
  readonly onClose: (kyc: KycConfirmed | undefined) => void;
  readonly onSignedOut: () => void;
  /** No passkeys on this phone: the passkey sheet's "not available here". */
  readonly onUnsupported: () => void;
  /** The bank's clock (`lib/bank-clock.ts`): Done's "Checked" when the receipt carries no time of its own. */
  readonly now: () => string | undefined;
  /** DCTA-199 ruling (Claude, 13 Oct): a card a passkey signs speaks "Check and sign." — the drawn card, to be read aloud. */
  readonly onCard?: ((bundleId: string) => void) | undefined;
}

interface Refresh {
  readonly id: string;
  /**
   * The next card needs a fresh refresh and a fresh consent: its card ran, its window ran out, or the bank would not declare
   * on it again. DCTA-198 (176-zz): a card cancelled (Back) does not spend it — the bank declares again on a dead card.
   */
  spent: boolean;
  /** It has built a card: a later declaration on it is a re-declaration (176-zz), whose 422 / 410 means "start afresh". */
  declared: boolean;
}

interface Live {
  readonly card: KycCard;
  readonly signer: KycSigner;
  prepared: Prepared | undefined;
  expiry: ReturnType<typeof setTimeout> | undefined;
  signed: boolean;
}

const status = (e: unknown): number | undefined => (e instanceof ApiError ? e.status : undefined);
const code = (e: unknown): string | undefined => (e instanceof ApiError ? e.reasonCode : undefined);
/** No answer at all, or the bank failed: nothing the bank decided, so nothing to quote. */
const unreachable = (e: unknown): boolean => !(e instanceof ApiError) || e.reasonCode === OFFLINE || e.status >= 500;

export class KycFlow {
  readonly view: KycFlowView;
  readonly #d: KycFlowDeps;
  #refresh: Refresh | undefined;
  /** After a spent refresh: once Myinfo answers, go straight to a fresh card with the customer's own details. */
  #redeclare: { readonly occupation: string; readonly funds: readonly string[] } | undefined;
  #live: Live | undefined;
  #kyc: KycConfirmed | undefined;
  /** What the demo Myinfo said it would share (the start's answer) — judge mode's Consent stage. */
  #myinfo: { readonly items: readonly string[]; readonly demo: boolean } | undefined;
  /** The card that was signed — judge mode's Proof reads its evidence. */
  #signedBundle: string | undefined;
  #busy = false;
  #over = false;
  /** Each step's request; an answer to an older one is let go (and a card it built is cancelled). */
  #seq = 0;
  /** The last card left behind, on its way to being cancelled — a re-declaration waits for it (176-zz). */
  #cancelling: Promise<unknown> | undefined;

  constructor(deps: KycFlowDeps) {
    this.#d = deps;
    this.view = new KycFlowView({
      onClose: () => this.#leave(),
      onGetMyinfo: () => void this.#getMyinfo(),
      onTalk: () => this.#d.onTalk(),
      onShare: () => void this.#share(),
      onCancelConsent: () => void this.#decline(),
      onBackToStart: () => this.#backToStart(),
      onReview: () => void this.#review(),
      onBackToDetails: () => this.#backToDetails(),
      onSign: () => this.#sign(),
      onNotNow: () => this.#notNow(),
      onDone: () => this.#finish(this.#kyc),
    });
    this.view.hero(startHero(deps.status, undefined));
  }

  /** A passkey sheet is up: nothing may take the flow away. */
  get busy(): boolean {
    return this.#live?.signer.signing === true;
  }

  /** F49: after Done — what judge mode's Proof needs to find the refresh's evidence and records (never the person's details). */
  proof(customerId: string): RefreshProof | undefined {
    const kyc = this.#kyc;
    const bundleId = this.#signedBundle;
    if (kyc === undefined || bundleId === undefined) return undefined;
    return { bundleId, kyc, fromStatus: this.#d.status, customerId, myinfo: this.#myinfo };
  }

  /**
   * Ask the bank to start, then show Start — or Done when the details are already up to date. "support": this bank has no
   * refresh (its routes exist only in demo mode) — the caller opens Talk to us instead.
   */
  async open(): Promise<"shown" | "support"> {
    const started = await this.#start();
    if (started === "support") return "support";
    this.view.go(started === "done" ? "done" : "start");
    return "shown";
  }

  /** Taken away from outside (a tab, the system's Back, a sign-out): the live card is cancelled; nothing saved. */
  close(): void {
    this.#seq += 1;
    this.#over = true;
    this.#drop("the customer left the identity check");
    this.view.consent(false);
  }

  // ------------------------------------------------------------------ start
  async #start(): Promise<"start" | "done" | "support" | "failed"> {
    const seq = ++this.#seq;
    this.view.starting(true);
    try {
      const r = await this.#d.api.kycRefreshStart();
      if (seq !== this.#seq) return "failed";
      this.#refresh = { id: r.refresh_id, spent: false, declared: false };
      this.#myinfo = r.myinfo;
      this.view.hero(startHero(this.#d.status, r.last_checked));
      this.view.note(undefined);
      return "start";
    } catch (e) {
      if (seq !== this.#seq) return "failed";
      if (code(e) === "AUTH_REQUIRED") {
        this.#d.onSignedOut();
        return "failed";
      }
      // §2.8: two tabs, or the flow finished elsewhere — nothing to do: Done straight away, claiming nothing more.
      if (code(e) === "KYC_REFRESH_NOT_NEEDED") {
        this.view.done(doneModel(undefined, undefined, undefined));
        return "done";
      }
      if (status(e) === 404) return "support";
      this.view.note(unreachable(e) ? "noBank" : "expired");
      return "failed";
    } finally {
      if (seq === this.#seq) this.view.starting(false);
    }
  }

  /** "Get my details from Myinfo" (or Try again): a live refresh → the consent placeholder; a spent one → start afresh. */
  async #getMyinfo(): Promise<void> {
    if (this.#busy || this.#over) return;
    this.#busy = true;
    try {
      if (this.#refresh === undefined || this.#refresh.spent) {
        const started = await this.#start();
        if (started === "done") {
          this.view.go("done");
          return;
        }
        if (started === "support") {
          this.#d.onTalk();
          return;
        }
        if (started === "failed") return;
      }
      this.view.consent(true);
    } finally {
      this.#busy = false;
    }
  }

  // ---------------------------------------------------- the consent prompt
  async #share(): Promise<void> {
    const refresh = this.#refresh;
    if (refresh === undefined || this.#busy || !this.view.consentOpen) return;
    this.#busy = true;
    const seq = ++this.#seq;
    this.view.consent(false);
    this.view.retrieving();
    this.view.go("details");
    try {
      const a = await this.#d.api.kycMyinfo(refresh.id, "given");
      if (seq !== this.#seq) return;
      if (a.status !== "details") {
        this.#toStart("declined");
        return;
      }
      const redo = this.#redeclare;
      const held = redo?.funds ?? a.profile.source_of_funds;
      this.view.details(
        personRows(a.person),
        redo?.occupation ?? a.profile.occupation,
        fundsOffered(a.options.source_of_funds).map((f) => ({ ...f, on: held.includes(f.key) })),
      );
      if (redo !== undefined) {
        this.#redeclare = undefined;
        await this.#declare(redo.occupation, redo.funds);
      }
    } catch (e) {
      if (seq !== this.#seq) return;
      this.#failedBeforeCard(e);
    } finally {
      this.#busy = false;
    }
  }

  /** Cancel (or Escape) at the prompt: nothing is shared; the bank records the customer's choice; Start says so. */
  async #decline(): Promise<void> {
    const refresh = this.#refresh;
    if (!this.view.consentOpen) return;
    this.view.consent(false);
    this.#redeclare = undefined;
    this.view.note("declined");
    this.view.focusGetMyinfo();
    if (refresh === undefined) return;
    // Recorded as declined (the bank's consent record); nothing was shared whatever it answers.
    await this.#d.api.kycMyinfo(refresh.id, "declined").catch(() => undefined);
  }

  // ---------------------------------------------------------------- Details
  #backToStart(): void {
    if (this.#busy) return;
    this.#seq += 1;
    this.view.go("start");
  }

  /** Review: the customer's own details are checked here (the bank is the check), then the bank builds the card. */
  async #review(): Promise<void> {
    if (this.#busy || this.#over) return;
    const occupation = this.view.occupation.trim();
    const funds = this.view.chosen();
    const problem = occupationProblem(occupation);
    this.view.occupationError(problem);
    this.view.fundsError(funds.length === 0 ? KYC_COPY.pickOne : undefined);
    if (problem !== undefined) {
      this.view.focusOccupation();
      return;
    }
    if (funds.length === 0) return;
    const refresh = this.#refresh;
    if (refresh === undefined || refresh.spent) {
      // One refresh, one card: a fresh refresh, and Myinfo's consent asked again; then straight to the card.
      this.#redeclare = { occupation, funds };
      await this.#getMyinfo();
      return;
    }
    this.#busy = true;
    let fresh = false;
    try {
      fresh = (await this.#declare(occupation, funds)) === "fresh";
    } finally {
      this.#busy = false;
    }
    if (fresh) {
      // 176-zz: the bank would not declare on this refresh again — a fresh refresh, Myinfo's consent asked again, then the card.
      this.#redeclare = { occupation, funds };
      await this.#getMyinfo();
    }
  }

  /**
   * The bank builds the card for these details on the live refresh. "fresh" (DCTA-198, 176-zz): a re-declaration the bank
   * would not take — 422 (its card can still be signed, or it ran) or 410 (the window passed) — the caller starts afresh.
   */
  async #declare(occupation: string, funds: readonly string[]): Promise<"done" | "fresh"> {
    const refresh = this.#refresh;
    if (refresh === undefined) return "done";
    const seq = ++this.#seq;
    this.view.building();
    this.view.go("sign");
    let bundleId: string | undefined;
    try {
      // 176-zz: the card left behind is cancelled at the bank first — the bank never sees it still signable beside this one.
      const cancelling = this.#cancelling;
      this.#cancelling = undefined;
      if (cancelling !== undefined) await cancelling;
      if (seq !== this.#seq) return "done";
      const made = await this.#d.api.kycDeclaration(refresh.id, { occupation, source_of_funds: funds });
      refresh.declared = true;
      bundleId = made.bundle_id;
      if (seq !== this.#seq) {
        void this.#cancel(made.bundle_id, "the customer stopped waiting before it was shown");
        return "done";
      }
      const bundle = await this.#d.api.bundle(made.bundle_id);
      if (seq !== this.#seq) {
        void this.#cancel(made.bundle_id, "the customer stopped waiting before it was shown");
        return "done";
      }
      const card = kycCardOf(bundle);
      if (card === undefined) {
        // A payload the screen cannot read is never drawn half: no card, no Sign — and nothing left to sign.
        void this.#cancel(made.bundle_id, "the card could not be shown");
        this.view.said(KYC_COPY.noBank.lead, KYC_COPY.noBank.rest, () => void this.#review());
        return "done";
      }
      this.#present(card);
      return "done";
    } catch (e) {
      if (seq !== this.#seq) return "done";
      if (bundleId !== undefined) void this.#cancel(bundleId, "the card could not be shown");
      if (refresh.declared && bundleId === undefined && (code(e) === "KYC_REFRESH_INVALID" || status(e) === 410)) {
        // 176-zz: the refresh declared before and the bank would not again — start afresh (the caller), from Details.
        refresh.spent = true;
        this.view.go("details");
        return "fresh";
      }
      if (code(e) === "KYC_REFRESH_INVALID" && e instanceof ApiError) {
        // The bank would not build it (a choice it never offered, details it does not hold): its sentence, on Details.
        refresh.spent = true;
        this.view.go("details");
        this.view.fundsError(e.serverMessage ?? KYC_COPY.pickOne);
        return "done";
      }
      this.#failedBeforeCard(e, () => void this.#review());
      return "done";
    }
  }

  // --------------------------------------------------------- Check and sign
  #present(card: KycCard): void {
    const live: Live = { card, signer: this.#d.signer(card.bundleId), prepared: undefined, expiry: undefined, signed: false };
    this.#live = live;
    this.view.card(card);
    this.#d.onCard?.(card.bundleId);
    // The payload lives 120 s (180 s in large type) — measured from now, ended a second early, as every card.
    const ttl = Date.parse(card.expiresAt) - Date.parse(card.createdAt);
    if (Number.isFinite(ttl) && ttl > 0) {
      live.expiry = setTimeout(() => {
        if (this.#live !== live || live.signer.signing) return;
        this.#drop("the card expired");
        this.#toStart("expired");
      }, Math.max(0, ttl - 1000));
    }
    void this.#prepare(live);
  }

  /** Before the tap: the ceremony's options; Sign holds (busy, label kept) until they land. */
  async #prepare(live: Live): Promise<void> {
    this.view.signBusy(true);
    try {
      live.prepared = await live.signer.prepare();
      if (this.#live === live && live.prepared === "first") this.view.signLine(ADD_COPY.firstSign);
    } catch (e) {
      if (this.#live !== live) return;
      this.#drop("the card could not be signed");
      if (code(e) === "AUTH_REQUIRED") this.#d.onSignedOut();
      else if (/EXPIRED/.test(code(e) ?? "")) this.#toStart("expired");
      else this.view.said(KYC_COPY.noBank.lead, KYC_COPY.noBank.rest, () => void this.#review());
    } finally {
      if (this.#live === live) this.view.signBusy(false);
    }
  }

  /** The Sign tap: its FIRST asynchronous act is the passkey ceremony (CardSigner) — nothing awaited before it. */
  #sign(): void {
    const live = this.#live;
    if (live === undefined || live.signed || this.#over) return;
    if (live.prepared === "unsupported") {
      this.#d.onUnsupported();
      return;
    }
    const ceremony = live.signer.sign(() => this.view.signLine(ADD_COPY.saved));
    if (ceremony === undefined) return;
    this.view.signStopped(undefined);
    this.view.signBusy(true);
    void ceremony.then((r) => this.#signed(live, r));
  }

  #signed(live: Live, r: SignResult): void {
    if (this.#live !== live) return;
    this.view.signBusy(false);
    switch (r.kind) {
      case "signed":
        this.#done(live, r.receipt);
        return;
      case "cancelled":
      case "timed-out":
      case "create-cancelled":
        // 25i (accepted as built): what did not happen, and the same Sign.
        this.view.signStopped("Nothing was signed.", " Sign when you're ready.");
        return;
      case "saved":
        this.view.signLine(ADD_COPY.saved);
        return;
      case "unsupported":
        this.#d.onUnsupported();
        return;
      case "expired":
        this.#drop("the card expired");
        this.#toStart("expired");
        return;
      case "failed":
        if (code(r.error) === "AUTH_REQUIRED") {
          this.#d.onSignedOut();
          return;
        }
        // The gateway refused the signature (its own sentence) or could not be reached: said above the same Sign.
        if (unreachable(r.error)) this.view.signStopped(KYC_COPY.noBank.lead, ` ${KYC_COPY.noBank.rest}`);
        else this.view.signStopped((r.error as ApiError).serverMessage ?? "Nothing was signed.", "");
        return;
    }
  }

  #done(live: Live, receipt: Receipt): void {
    live.signed = true;
    this.#signedBundle = live.card.bundleId;
    if (live.expiry !== undefined) clearTimeout(live.expiry);
    live.expiry = undefined;
    this.#kyc = receipt.kyc;
    // The rows are written while the screen is still out of view; the status region changes as it arrives (announced).
    this.view.go("done", false);
    this.view.done(doneModel(this.#kyc, live.card, receipt.as_of ?? this.#d.now()));
    this.view.go("done");
  }

  #notNow(): void {
    if (this.busy) return;
    this.#drop("the customer chose not now on the identity-check card");
    this.#finish(undefined);
  }

  /** DCTA-198 (176-zz): the card is cancelled at the bank; the refresh stays live — the next Review declares on it again. */
  #backToDetails(): void {
    if (this.busy) return;
    this.#seq += 1;
    this.#drop("the customer went back from the identity-check card");
    this.view.go("details");
  }

  // ------------------------------------------------------------------ ends
  /** Something stopped the flow before a card existed: Start, with the line for what happened. */
  #failedBeforeCard(e: unknown, retry?: () => void): void {
    if (code(e) === "AUTH_REQUIRED") {
      this.#d.onSignedOut();
      return;
    }
    if (code(e) === "MYINFO_UNAVAILABLE") {
      this.#toStart("unavailable");
      return;
    }
    // Past its window (410), or unknown to the bank (404 — swept): nothing was saved; the next try starts afresh.
    if (status(e) === 410 || status(e) === 404) {
      this.#toStart("expired");
      return;
    }
    if (retry !== undefined && this.view.screen === "sign") {
      this.view.said(KYC_COPY.noBank.lead, KYC_COPY.noBank.rest, retry);
      return;
    }
    this.#toStart(unreachable(e) ? "noBank" : "expired");
  }

  #toStart(note: StartNote): void {
    if (note === "expired" && this.#refresh !== undefined) this.#refresh.spent = true;
    this.#redeclare = undefined;
    this.view.consent(false);
    this.view.note(note);
    this.view.go("start", false);
    this.view.focusGetMyinfo();
  }

  /** Close (×) or Escape: nothing saved — not while a passkey sheet is up. */
  #leave(): void {
    if (this.busy) return;
    this.#drop("the customer closed the identity check");
    this.#finish(undefined);
  }

  #finish(kyc: KycConfirmed | undefined): void {
    if (this.#over) return;
    this.#over = true;
    this.#seq += 1;
    this.#d.onClose(kyc);
  }

  /** The live card goes: its timer stops, and — unless it was signed — it is cancelled at the bank. */
  #drop(reason: string): void {
    const live = this.#live;
    this.#live = undefined;
    if (live === undefined) return;
    if (live.expiry !== undefined) clearTimeout(live.expiry);
    if (!live.signed) this.#cancelling = this.#cancel(live.card.bundleId, reason);
  }

  #cancel(bundleId: string, reason: string): Promise<unknown> {
    return this.#d.api.cancelBundle(bundleId, reason).catch(() => undefined);
  }
}
